import { computed, reactive } from 'vue';
import { store } from '@/store';
import { useConditions } from '@shared/composables/useConditions';
import { ConditionPickerOption, ConditionDataType } from '@/shared/components/conditions/models/conditions';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';

export enum TriggerConditionAttributeType {
  MessageText = 'message.text',
  MessageIntent = 'message.intentId',
  EventSource = 'event.source',
  CurrentPageUrl = 'appUser.currentUrl',
  ChannelIdentity = 'event.channelIdentity',
  TimeOnPage = 'timeOnPage',
}

export function useTriggerConditions() {
  const state = reactive({
    conditionsToDisplay: [] as ConditionPickerOption[],
    allConditions: [
      {
        id: TriggerConditionAttributeType.MessageText,
        title: 'Message Text',
        type: ConditionDataType.Text,
        icon: 'MessageIcon',
      },
      {
        id: TriggerConditionAttributeType.MessageIntent,
        title: 'Message Intent',
        type: ConditionDataType.Intent,
        icon: 'MessageIcon',
      },
      {
        id: TriggerConditionAttributeType.EventSource,
        title: 'Channel',
        type: ConditionDataType.List,
        icon: 'ListIcon',
      },
      {
        id: TriggerConditionAttributeType.ChannelIdentity,
        title: 'Channel Identity',
        type: ConditionDataType.List,
        icon: 'ListIcon',
      },
      {
        id: TriggerConditionAttributeType.CurrentPageUrl,
        title: 'Current Page URL',
        type: ConditionDataType.Text,
        icon: 'PageIcon',
      },
      {
        id: TriggerConditionAttributeType.TimeOnPage,
        title: 'Time on Page',
        type: ConditionDataType.Duration,
        icon: 'DurationIcon',
      },
    ] as ConditionPickerOption[],
    channelOptions: [
      { id: 'sms-bt', title: 'BT Smart Messaging Tailored' },
      { id: 'sms-govuknotify', title: 'GOV.UK Notify SMS' },
      { id: 'instagram', title: 'Instagram' },
      { id: 'messenger', title: 'Messenger' },
      { id: 'web-assistant', title: 'Web Assistant' },
      { id: 'whatsapp', title: 'WhatsApp' },
    ],
  });

  // Override computed properties
  const conditions = computed(() => state.allConditions);
  const visibleConditions = computed(() => state.conditionsToDisplay);

  const base = useConditions([], conditions);

  // Extend base functionality
  const excludeConditions = (excludedConditions: string[]) => {
    state.conditionsToDisplay =
      excludedConditions.length > 0
        ? state.allConditions.filter((c: ConditionPickerOption) => !excludedConditions.includes(c.id))
        : [...state.allConditions];
  };

  const getConditionOptions = (condition: ConditionViewModel) => {
    if (condition.attribute === TriggerConditionAttributeType.EventSource) {
      return state.channelOptions;
    } else if (condition.attribute === TriggerConditionAttributeType.ChannelIdentity) {
      const channelIds = store.getters.channelIdentities;
      return channelIds.map((c: ChannelIdentityViewModel) => ({
        id: c.id,
        title: c.name,
      }));
    }
    return base.getConditionOptions(condition);
  };

  const getListConditionValueTitle = (condition: ConditionViewModel) => {
    if (condition.attribute === TriggerConditionAttributeType.EventSource) {
      const channel = state.channelOptions.find((c: { id: string; title: string }) => c.id === condition.value);
      return channel?.title || condition.value;
    }
    if (condition.attribute === TriggerConditionAttributeType.ChannelIdentity) {
      const channelIdentity = store.getters.channelIdentities.find(
        (c: ChannelIdentityViewModel) => c.id === condition.value,
      );
      return channelIdentity?.name || condition.value;
    }
    return base.getListConditionValueTitle(condition);
  };

  const isConditionValid = (condition: ConditionViewModel): boolean => {
    if (condition.attribute.startsWith('event.channelIdentity') && condition.value) {
      return store.getters.channelIdentities.some((c: ChannelIdentityViewModel) => c.id === condition.value);
    }
    if (condition.type === 'intent' && condition.value) {
      return !!store.getters.getIntentName(condition.value);
    }
    return base.isConditionValid(condition);
  };

  // Initialize
  state.conditionsToDisplay = [...state.allConditions];

  return {
    ...base, // Spread base functionality
    conditions,
    visibleConditions,
    excludeConditions,
    getConditionOptions,
    getListConditionValueTitle,
    isConditionValid,
  };
}
